import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { NgApexchartsModule } from "ng-apexcharts";
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexPlotOptions,
  ApexXAxis,
  ApexYAxis,
  ApexFill,
  ApexTooltip,
  ApexResponsive,
  ApexLegend,
} from "ng-apexcharts";

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  fill: ApexFill;
  tooltip: ApexTooltip;
  responsive: ApexResponsive[];
  legend: ApexLegend;
  colors: string[];
};

@Component({
  selector: "risk-summary-chart",
  standalone: true,
  imports: [CommonModule, NgApexchartsModule],
  template: `
    <div #chartContainer class="w-full">
      <apx-chart
        *ngIf="chartOptions"
        [series]="chartOptions.series"
        [chart]="chartOptions.chart"
        [plotOptions]="chartOptions.plotOptions"
        [dataLabels]="chartOptions.dataLabels"
        [xaxis]="chartOptions.xaxis"
        [yaxis]="chartOptions.yaxis"
        [colors]="chartOptions.colors"
        [tooltip]="chartOptions.tooltip"
        [legend]="chartOptions.legend"
      ></apx-chart>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
      }
    `,
  ],
})
export class RiskSummaryChartComponent implements OnChanges, AfterViewInit {
  @Output() dataEvent = new EventEmitter<any>();
  @Input() chartData: { label: string; count: number }[] = [];
  @ViewChild("chartContainer") chartContainer!: ElementRef<HTMLDivElement>;

  public chartOptions: Partial<ChartOptions> | any;

  colorMap: Record<string, string> = {
    critical: "#D61409",
    high: "#F86522",
    medium: "#FFBD40",
    low: "#00B25A",
  };

  defaultColor = "#9CA3AF";

  ngAfterViewInit() {
    this.updateChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["chartData"]) {
      this.updateChart();
    }
  }

private updateChart(): void {
  if (!this.chartData || this.chartData.length === 0) return;

  const labels = this.chartData.map((d) => d.label);
  const values = this.chartData.map((d) => d.count);
  const colors = this.chartData.map(
    (d) => this.colorMap[d.label.toLowerCase()] || this.defaultColor
  );

  // Detect if the last two labels are the same
  const hasDuplicateLastLabel =
    labels.length > 1 &&
    labels[labels.length - 1].toLowerCase() ===
      labels[labels.length - 2].toLowerCase();

  // Base height (each bar = 60px)
  let chartHeight = Math.max(200, this.chartData.length * 60);

  // Add extra padding if last two labels are same
  if (hasDuplicateLastLabel) {
    chartHeight += 40; // add some space at bottom
  }

  const maxCount = Math.max(...values);
  const paddedMax = maxCount * 1.1;

  this.chartOptions = {
    series: [
      {
        name: "Count",
        data: values,
      },
    ],
    chart: {
      type: "bar",
      height: chartHeight,
      toolbar: { show: false },
      // FIX: Add margin to the bottom to prevent category label overlap
      margin: {
          top: 0,
          right: 0,
          bottom: 30,
          left: 0
      },
      events: {
        dataPointSelection: (_event: any, _chartContext: any, config: any) => {
          const item = this.chartData[config.dataPointIndex];
          if (item) {
            this.dataEvent.emit(item);
          }
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 2,
        distributed: true,
        barHeight: "70%",
        dataLabels: {
          position: "top",
        },
        colors: {
          ranges: [],
          backgroundBarColors: [],
        },
      },
    },
    dataLabels: {
      enabled: true,
      textAnchor: "start",
      offsetX: 10,
      style: {
        colors: ["#111"],
        fontWeight: 700,
        fontSize: "14px",
      },
      formatter: (val: number) => val.toString(),
    },
    xaxis: {
      categories: labels,
      // ✅ NEW FIX: Explicitly set minimum to 0 to prevent negative values
      min: 0,
      // Previous fix: Apply the padded max value
      max: paddedMax,
      labels: {
        style: {
          fontSize: "11px",
          fontWeight: 700,
          colors: "#111",
        },
      },
      axisBorder: {
        show: true, 
        color: "#333",
        height: 2,
      },
      axisTicks: {
        show: true, 
        color: "#333",
      },
    },
    yaxis: {
      labels: {
        style: {
          fontSize: "14px",
          fontWeight: 600,
          colors: "#111",
        },
      },
    },
    colors,
    fill: {
      type: "solid",
      opacity: 1,
    },
    tooltip: {
      enabled: false,
    },
    legend: {
      show: false,
    },
    theme: {
      mode: "light",
      monochrome: {
        enabled: false,
      },
    },
  };
}

}
